import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { supabase } from '@/lib/supabase';
import { Sentry, sentryEnabled } from '@/lib/sentry';

const API_BASE_URL = import.meta.env.VITE_API_URL;

let cachedAccessToken: string | null = null;

supabase.auth.getSession().then(({ data: { session } }) => {
  cachedAccessToken = session?.access_token ?? null;
});

supabase.auth.onAuthStateChange((_event, session) => {
  cachedAccessToken = session?.access_token ?? null;
});

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    if (!cachedAccessToken) {
      throw new Error('Authentication required');
    }
    config.headers.Authorization = `Bearer ${cachedAccessToken}`;
    if (sentryEnabled) {
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        level: 'info',
        data: { method: config.method, url: config.url },
      });
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    if (sentryEnabled) {
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        level: 'info',
        data: {
          method: response.config.method,
          url: response.config.url,
          status: response.status,
        },
      });
    }
    return response;
  },
  (error) => {
    if (sentryEnabled && axios.isAxiosError(error)) {
      const status = error.response?.status;
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        level: 'error',
        data: {
          method: error.config?.method,
          url: error.config?.url,
          status,
        },
      });
      if (status && status >= 500) {
        Sentry.withScope((scope) => {
          scope.setTag('http.status', String(status));
          scope.setTag('http.url', error.config?.url ?? 'unknown');
          Sentry.captureException(error);
        });
      }
    }
    return Promise.reject(error);
  }
);

type RequestMethod = 'get' | 'post' | 'put' | 'delete' | 'patch';

export type ApiError = {
  status: number;
  message: string;
  code?: string;
  details?: unknown;
};

const formatError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 500;
    const message =
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      error.message ||
      'An error occurred';
    const code = error.response?.data?.error?.code || undefined;
    return { status, message, code, details: error.response?.data };
  }
  if (error instanceof Error) {
    return { status: 500, message: error.message || 'An unknown error occurred' };
  }
  return { status: 500, message: 'An unexpected error occurred' };
};

const handleRequest = async <T>(
  method: RequestMethod,
  endpoint: string,
  data: Record<string, unknown> | FormData | null = null,
  params: Record<string, unknown> = {}
): Promise<T> => {
  try {
    const config: AxiosRequestConfig = {
      method,
      url: endpoint,
      data: data && (data instanceof FormData || Object.keys(data).length > 0) ? data : undefined,
      params: params && Object.keys(params).length > 0 ? params : undefined,
    };
    if (data instanceof FormData) {
      config.headers = { 'Content-Type': 'multipart/form-data' };
    }
    const response: AxiosResponse<T> = await apiClient(config);
    return response.data;
  } catch (error) {
    throw formatError(error);
  }
};

const api = {
  get: <T>(endpoint: string, params?: Record<string, unknown>) =>
    handleRequest<T>('get', endpoint, null, params),
  post: <T>(endpoint: string, data?: Record<string, unknown> | FormData) =>
    handleRequest<T>('post', endpoint, data),
  put: <T>(endpoint: string, data?: Record<string, unknown>) =>
    handleRequest<T>('put', endpoint, data),
  delete: <T>(endpoint: string, params?: Record<string, unknown>) =>
    handleRequest<T>('delete', endpoint, null, params),
  patch: <T>(endpoint: string, data?: Record<string, unknown>) =>
    handleRequest<T>('patch', endpoint, data),
};

export default api;
